import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Search, Link as LinkIcon, CheckCircle, Clock, Megaphone, MoreHorizontal, Play, Pause, Archive, ArrowUpDown, ArrowUp, ArrowDown, ExternalLink, CalendarDays, Eye, PenLine, Gift } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { logUpdate } from '@/lib/audit-log';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { DataPagination } from '@/components/DataPagination';
import { usd } from '@/lib/refunds';
import { ViralRemakeBadge, isViralRemake, remakeDescription, WHAT_TO_REMAKE_LABEL, VIDEOS_TO_REMAKE_HEADING, MultiProductBadge, isMultiProduct, TiktokCollabTypeBadge } from '@/components/ViralRemakeBadge';
import { format } from 'date-fns';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

type CampaignStatus = 'draft' | 'pending' | 'active' | 'paused' | 'archived';
type TabFilter = 'all' | CampaignStatus;
type SortField = 'name' | 'brand_name' | 'created_at';
type SortDir = 'asc' | 'desc';

type Campaign = {
  id: string;
  name?: string;
  status: CampaignStatus;
  tap_affiliate_link?: string | null;
  // Optional, brand-reported (2026-09-25) — see TiktokCollabTypeBadge.
  tiktok_collab_type?: string | null;
  created_at: string;
  brand_id?: string | null;
  brand_configs?: { id: string; brand_name: string; contact_email?: string | null } | null;
  // Viral Video Remake job (read-only here). The list query selects '*', so no
  // select change was needed — this only documents the column. Missing/null on
  // older cached rows reads as a standard campaign via isViralRemake().
  is_viral_remake?: boolean | null;
  // Per-campaign creator counts, computed via embedded aggregates in the list
  // query (no extra round-trips). Definitions mirror RosterCampaignDetail:
  //   hired = cr_hires confirmed|completed|partial_completed,
  //   invited = cr_hires invited, waitlisted = cr_hires waitlisted,
  //   applications = all cr_applications rows for the campaign.
  hired_count?: number;
  invited_count?: number;
  waitlisted_count?: number;
  applications_count?: number;
  // Multi-product campaigns — embedded via the list query's select, so this
  // is a free read, not an extra round trip. Absent/short means single-product.
  cr_campaign_products?: { id: string }[];
  // Creator Bonus (2026-09-29, optional) — the list query selects '*', so no
  // select change was needed. Both null = no bonus.
  bonus_amount_cents?: number | null;
  bonus_gmv_threshold_cents?: number | null;
  [key: string]: any;
};

// Resolves the search box's text into a Postgrest `.or(...)` filter string
// matching either the campaign name or its brand's name (brand_configs is a
// separate table, so brand matches need an async lookup first). Returns null
// for an empty search. Shared by the paginated rows query and the tab-count
// query so both filter identically — count buckets stay in sync with what's
// on screen.
//
// Deliberately returns a plain string, NOT the query builder itself: Supabase
// query builders are "thenables" (lazy — the request only fires when
// awaited/`.then()`'d), so returning one through this async function's
// `return` would have the `await` at the call site adopt its resolution and
// fire the query early, handing callers back the resolved {data,error}
// response instead of a chainable builder — breaking any `.range()`/etc.
// called afterward. A plain string return sidesteps that entirely; callers
// apply `.or(searchFilter)` themselves, synchronously.
async function buildCampaignSearchFilter(search: string): Promise<string | null> {
  const q = search.trim();
  if (!q) return null;

  const { data: matchedBrands } = await supabase
    .from('brand_configs')
    .select('id')
    .ilike('brand_name', `%${q}%`);
  const brandIds = (matchedBrands ?? []).map((b: any) => b.id);

  return brandIds.length > 0
    ? `name.ilike.%${q}%,brand_id.in.(${brandIds.join(',')})`
    : `name.ilike.%${q}%`;
}

function isValidUrl(value: string) {
  if (!value.trim()) return true;
  const v = value.trim();
  try {
    const url = new URL(v.includes('://') ? v : `https://${v}`);
    return url.hostname.includes('.');
  } catch {
    return false;
  }
}

function StatusBadge({ status }: { status: string }) {
  if (status === 'draft')    return <Badge className="bg-slate-100 text-slate-600 border border-slate-200">Draft</Badge>;
  if (status === 'active')   return <Badge className="bg-green-100 text-green-800 border border-green-200">Active</Badge>;
  if (status === 'paused')   return <Badge className="bg-orange-100 text-orange-800 border border-orange-200">Paused</Badge>;
  if (status === 'archived') return <Badge className="bg-gray-100 text-gray-600 border border-gray-200">Archived</Badge>;
  if (status === 'pending')  return <Badge className="bg-yellow-100 text-yellow-800 border border-yellow-200">Pending</Badge>;
  return <Badge variant="outline">{status}</Badge>;
}

export default function RosterCampaigns() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState<TabFilter>('all');
  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [editTarget, setEditTarget] = useState<Campaign | null>(null);
  const [tapLinkInput, setTapLinkInput] = useState('');
  const [statusConfirm, setStatusConfirm] = useState<{ campaign: Campaign; status: CampaignStatus } | null>(null);

  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  function handleTabChange(tab: TabFilter) {
    setActiveTab(tab);
    setPage(1);
    setSearchInput('');
    setSearch('');
  }

  function handleSort(field: SortField) {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
    setPage(1);
  }

  const { data, isLoading } = useQuery({
    queryKey: ['roster-campaigns', activeTab, search, sortField, sortDir, page, pageSize],
    queryFn: async () => {
      // Creator counts ride along on the SAME query as embedded rows (no extra
      // round-trips) — same one-to-many embed style as brand_configs above.
      // We pull just `status` for each related hire/application and tally them
      // client-side; hired = confirmed|completed, invited = invited,
      // applications = every cr_applications row (mirrors RosterCampaignDetail).
      let query = supabase
        .from('cr_campaigns')
        .select(
          '*, brand_configs(id, brand_name, contact_email), ' +
          'cr_hires(status), ' +
          'cr_applications(status), ' +
          'cr_campaign_products(id, product_id, product_name, product_description, product_image_urls, sort_order)',
          { count: 'exact' }
        )
        .order(sortField !== 'brand_name' ? sortField : 'created_at', { ascending: sortField !== 'brand_name' ? sortDir === 'asc' : false });

      if (activeTab !== 'all') {
        query = query.eq('status', activeTab);
      }

      const searchFilter = await buildCampaignSearchFilter(search);
      if (searchFilter) query = query.or(searchFilter);

      const { data: rows, count, error } = await query
        .range((page - 1) * pageSize, page * pageSize - 1);
      if (error) throw error;

      // Tally the embedded hire/application rows into counts. 'partial_completed'
      // (a completed hire that dropped back below videos_required after an
      // admin rejected one of its videos) counts as hired here, same as
      // RosterCampaignDetail's hiredCount — omitting it undercounted "Hired"
      // on this list versus the detail page for any campaign with one.
      let result = (rows ?? []).map((r: any) => {
        const hires = Array.isArray(r.cr_hires) ? r.cr_hires : [];
        const apps = Array.isArray(r.cr_applications) ? r.cr_applications : [];
        return {
          ...r,
          hired_count: hires.filter((h: any) => h.status === 'confirmed' || h.status === 'completed' || h.status === 'partial_completed').length,
          invited_count: hires.filter((h: any) => h.status === 'invited').length,
          waitlisted_count: hires.filter((h: any) => h.status === 'waitlisted').length,
          applications_count: apps.length,
        };
      }) as Campaign[];
      if (sortField === 'brand_name') {
        result = [...result].sort((a, b) => {
          const aN = (a.brand_configs?.brand_name ?? '').toLowerCase();
          const bN = (b.brand_configs?.brand_name ?? '').toLowerCase();
          return sortDir === 'asc' ? aN.localeCompare(bN) : bN.localeCompare(aN);
        });
      }

      return { rows: result, total: count ?? 0 };
    },
  });

  const { data: counts } = useQuery({
    // search is part of the key so the per-status tallies below refetch and
    // reflect the same rows the search box is currently narrowing the table
    // to — otherwise the tab counts kept showing the whole table's totals
    // no matter what was typed in. Deliberately excludes activeTab: these
    // counts are the per-status breakdown shown across ALL tabs, not just
    // the one currently selected.
    queryKey: ['roster-campaigns-counts', search],
    queryFn: async () => {
      let query = supabase.from('cr_campaigns').select('status');
      const searchFilter = await buildCampaignSearchFilter(search);
      if (searchFilter) query = query.or(searchFilter);
      const { data, error } = await query;
      if (error) throw error;
      const all = data ?? [];
      return {
        total:    all.length,
        draft:    all.filter(r => r.status === 'draft').length,
        pending:  all.filter(r => r.status === 'pending').length,
        active:   all.filter(r => r.status === 'active').length,
        paused:   all.filter(r => r.status === 'paused').length,
        archived: all.filter(r => r.status === 'archived').length,
      };
    },
    staleTime: 30_000,
  });

  const saveMutation = useMutation({
    mutationFn: async ({ id, tap_affiliate_link, activate, old_tap_affiliate_link, old_status, campaign_name }: {
      id: string;
      tap_affiliate_link: string;
      activate: boolean;
      old_tap_affiliate_link?: string | null;
      old_status?: string;
      campaign_name?: string;
    }) => {
      const update: any = { tap_affiliate_link: tap_affiliate_link || null };
      if (activate) update.status = 'active';
      const { error } = await supabase.from('cr_campaigns').update(update).eq('id', id);
      if (error) throw error;

      await logUpdate(
        'cr_campaigns',
        id,
        { tap_affiliate_link: old_tap_affiliate_link ?? null, ...(activate ? { status: old_status } : {}) },
        { tap_affiliate_link: tap_affiliate_link || null, ...(activate ? { status: 'active' } : {}) },
        { description: activate ? 'Admin set TAP link and activated campaign' : 'Admin updated TAP affiliate link', campaign_name }
      );
    },
    onSuccess: (_, vars) => {
      toast.success(vars.activate ? 'Campaign activated' : 'TAP link saved');
      queryClient.invalidateQueries({ queryKey: ['roster-campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['roster-campaigns-counts'] });
      setEditTarget(null);
      setTapLinkInput('');
    },
    onError: (err: any) => toast.error(err.message || 'Save failed'),
  });

  const statusMutation = useMutation({
    mutationFn: async ({ id, status, tap_affiliate_link, old_status, campaign_name }: {
      id: string;
      status: CampaignStatus;
      tap_affiliate_link?: string | null;
      old_status?: string;
      campaign_name?: string;
    }) => {
      // if (status === 'active' && !tap_affiliate_link) throw new Error('Add a TAP affiliate link before activating this campaign.');
      const { error } = await supabase.from('cr_campaigns').update({ status }).eq('id', id);
      if (error) throw error;

      await logUpdate(
        'cr_campaigns',
        id,
        { status: old_status },
        { status },
        { description: `Admin changed campaign status to ${status}`, campaign_name }
      );
    },
    onSuccess: (_, vars) => {
      const labels: Record<CampaignStatus, string> = {
        draft:    'Campaign set to draft',
        active:   'Campaign activated',
        paused:   'Campaign paused',
        archived: 'Campaign archived',
        pending:  'Campaign set to pending',
      };
      toast.success(labels[vars.status]);
      queryClient.invalidateQueries({ queryKey: ['roster-campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['roster-campaigns-counts'] });
      setStatusConfirm(null);
    },
    onError: (err: any) => toast.error(err.message || 'Status update failed'),
  });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  function openEdit(campaign: Campaign) {
    setEditTarget(campaign);
    setTapLinkInput(campaign.tap_affiliate_link ?? '');
  }

  function closeEdit() {
    setEditTarget(null);
    setTapLinkInput('');
  }

  const campaignName = (c: Campaign) => c.name || c.id;

  return (
    <div className="h-full flex flex-col gap-6">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Roster Jobs</h1>
        <p className="text-muted-foreground text-sm">Manage creator roster jobs and TAP links</p>
      </div>

      <div className="grid grid-cols-2 gap-4 shrink-0 sm:grid-cols-3 lg:grid-cols-6">
        {([
          { label: 'Total',    value: counts?.total,    color: '',                       icon: <Megaphone className="h-4 w-4" /> },
          { label: 'Draft',    value: counts?.draft,    color: 'text-slate-500',         icon: <Clock className="h-4 w-4" /> },
          { label: 'Pending',  value: counts?.pending,  color: 'text-yellow-600',        icon: <Clock className="h-4 w-4" /> },
          { label: 'Active',   value: counts?.active,   color: 'text-green-600',         icon: <CheckCircle className="h-4 w-4" /> },
          { label: 'Paused',   value: counts?.paused,   color: 'text-orange-500',        icon: <Pause className="h-4 w-4" /> },
          { label: 'Archived', value: counts?.archived, color: 'text-muted-foreground',  icon: <Archive className="h-4 w-4" /> },
        ] as const).map(({ label, value, color, icon }) => (
          <Card key={label}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                {icon} {label}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className={`text-2xl font-bold ${color}`}>{value ?? '–'}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="flex-1 flex flex-col min-h-0">
        <CardContent className="pt-4 flex-1 flex flex-col min-h-0">
          <div className="flex flex-col gap-3 mb-4 shrink-0 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
            <Tabs value={activeTab} onValueChange={v => handleTabChange(v as TabFilter)}>
              <TabsList>
                <TabsTrigger value="all">All ({counts?.total ?? 0})</TabsTrigger>
                <TabsTrigger value="draft">Draft ({counts?.draft ?? 0})</TabsTrigger>
                <TabsTrigger value="pending">Pending ({counts?.pending ?? 0})</TabsTrigger>
                <TabsTrigger value="active">Active ({counts?.active ?? 0})</TabsTrigger>
                <TabsTrigger value="paused">Paused ({counts?.paused ?? 0})</TabsTrigger>
                <TabsTrigger value="archived">Archived ({counts?.archived ?? 0})</TabsTrigger>
              </TabsList>
            </Tabs>
            <div className="relative w-72">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by campaign or brand name..."
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                className="pl-8"
              />
            </div>
          </div>

          <div className="flex-1 overflow-auto min-h-0">
            <Table>
              <TableHeader>
                <TableRow>
                  {(['name', 'brand_name'] as SortField[]).map((field) => (
                    <TableHead
                      key={field}
                      className="cursor-pointer select-none whitespace-nowrap"
                      onClick={() => handleSort(field)}
                    >
                      <span className="inline-flex items-center gap-1">
                        {field === 'name' ? 'Campaign' : 'Brand'}
                        {sortField === field
                          ? sortDir === 'asc' ? <ArrowUp className="h-3.5 w-3.5" /> : <ArrowDown className="h-3.5 w-3.5" />
                          : <ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground/50" />}
                      </span>
                    </TableHead>
                  ))}
                  <TableHead>Status</TableHead>
                  <TableHead className="text-center whitespace-nowrap">Hired</TableHead>
                  <TableHead className="text-center whitespace-nowrap">Invited</TableHead>
                  <TableHead className="text-center whitespace-nowrap">Applications</TableHead>
                  <TableHead className="text-right whitespace-nowrap">Bonus</TableHead>
                  <TableHead className="cursor-pointer select-none whitespace-nowrap" onClick={() => handleSort('created_at')}>
                    <span className="inline-flex items-center gap-1">
                      Created
                      {sortField === 'created_at'
                        ? sortDir === 'asc' ? <ArrowUp className="h-3.5 w-3.5" /> : <ArrowDown className="h-3.5 w-3.5" />
                        : <ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground/50" />}
                    </span>
                  </TableHead>
                  <TableHead className="whitespace-nowrap">Posted</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={10} className="h-24 text-center text-muted-foreground">Loading...</TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} className="h-24 text-center text-muted-foreground">
                      {search ? 'No campaigns match your search' : activeTab === 'all' ? 'No campaigns found' : `No ${activeTab} campaigns`}
                    </TableCell>
                  </TableRow>
                ) : rows.map((c) => (
                  <TableRow key={c.id} className="cursor-pointer hover:bg-muted/50" onClick={() => navigate(`/roster-campaigns/${c.id}`)}>
                    <TableCell className="font-medium max-w-[280px]">
                      {/* Badges stack UNDER the name rather than beside it
                          (2026-09-25) — three possible badges wide, each
                          shrink-0, on one flex line with no wrap reliably
                          overran max-w-[280px]: a td's max-width is only
                          advisory in an auto-layout table, so the overflow
                          didn't clip, it bled visually into the Brand column.
                          A wrapped row under the (still single-line,
                          truncated) name can't force the cell wider — it
                          wraps within it instead, same width for zero, one or
                          three badges. */}
                      <div className="min-w-0">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="block truncate">{campaignName(c)}</span>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-xs break-words">
                              {campaignName(c)}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        {(isViralRemake(c) || isMultiProduct(c) || c.tiktok_collab_type) && (
                          <div className="flex flex-wrap items-center gap-1.5 mt-1">
                            {/* Full label per product feedback — widened the cell
                                (was 220px) rather than re-abbreviating the badge. */}
                            {isViralRemake(c) && <ViralRemakeBadge className="shrink-0" />}
                            {isMultiProduct(c) && <MultiProductBadge className="shrink-0" count={c.cr_campaign_products.length} />}
                            <TiktokCollabTypeBadge value={c.tiktok_collab_type} className="shrink-0" />
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">
                      {c.brand_configs?.brand_name ?? <span className="text-muted-foreground italic">–</span>}
                      {c.brand_configs?.contact_email && (
                        <div className="text-xs text-muted-foreground">{c.brand_configs.contact_email}</div>
                      )}
                    </TableCell>
                    <TableCell><StatusBadge status={c.status} /></TableCell>
                    <TableCell className="text-center tabular-nums font-medium">
                      {c.hired_count ? c.hired_count : <span className="text-muted-foreground">0</span>}
                    </TableCell>
                    <TableCell className="text-center tabular-nums font-medium">
                      {c.invited_count ? c.invited_count : <span className="text-muted-foreground">0</span>}
                    </TableCell>
                    <TableCell className="text-center tabular-nums font-medium">
                      {c.applications_count ? c.applications_count : <span className="text-muted-foreground">0</span>}
                    </TableCell>
                    <TableCell className="text-right text-sm tabular-nums whitespace-nowrap">
                      {c.bonus_amount_cents ? (
                        <span
                          className="inline-flex items-center justify-end gap-1 text-amber-500 font-medium"
                          title={c.bonus_gmv_threshold_cents ? `At ${usd(c.bonus_gmv_threshold_cents)} GMV` : undefined}
                        >
                          <Gift className="h-3 w-3" />
                          {usd(c.bonus_amount_cents)}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">–</span>
                      )}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                      {c.created_at ? format(new Date(c.created_at), 'MMM d, yyyy') : '–'}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                      {c.posted_at ? format(new Date(c.posted_at), 'MMM d, yyyy') : <span className="italic">–</span>}
                    </TableCell>
                    <TableCell className="text-right" onClick={e => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0"
                          title="View campaign detail"
                          onClick={() => setSelectedCampaign(c)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => openEdit(c)}>
                          {c.tap_affiliate_link ? 'Edit TAP Link' : 'Add TAP Link'}
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button size="sm" variant="ghost" className="h-8 w-8 p-0">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              disabled={c.status === 'active' || statusMutation.isPending}
                              onClick={() => setStatusConfirm({ campaign: c, status: 'active' })}
                              className="gap-2"
                            >
                              <Play className="h-3.5 w-3.5 text-green-600" /> Set Active
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              disabled={c.status === 'paused' || statusMutation.isPending}
                              onClick={() => setStatusConfirm({ campaign: c, status: 'paused' })}
                              className="gap-2"
                            >
                              <Pause className="h-3.5 w-3.5 text-orange-500" /> Set Paused
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              disabled={c.status === 'archived' || statusMutation.isPending}
                              onClick={() => setStatusConfirm({ campaign: c, status: 'archived' })}
                              className="gap-2 text-destructive focus:text-destructive"
                            >
                              <Archive className="h-3.5 w-3.5" /> Archive
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <DataPagination
            currentPage={page}
            totalPages={totalPages}
            pageSize={pageSize}
            totalItems={total}
            onPageChange={setPage}
            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            className="mt-auto shrink-0"
          />
        </CardContent>
      </Card>

      <Dialog open={!!editTarget} onOpenChange={(open) => { if (!open) closeEdit(); }}>
        <DialogContent className="sm:max-w-md overflow-hidden">
          <DialogHeader className="min-w-0">
            <DialogTitle>{editTarget?.tap_affiliate_link ? 'Edit TAP Link' : 'Add TAP Link'}</DialogTitle>
          </DialogHeader>
          {editTarget && (
            <div className="space-y-4 py-2">
              <p className="text-sm text-muted-foreground font-medium break-all">{campaignName(editTarget)}</p>
              <div className="space-y-1.5">
                <Label htmlFor="tap-link">TAP Link</Label>
                <Input
                  id="tap-link"
                  placeholder="https://affiliate.tiktok.com/..."
                  value={tapLinkInput}
                  onChange={e => setTapLinkInput(e.target.value)}
                  className={!isValidUrl(tapLinkInput) ? 'border-red-500 focus-visible:ring-red-500' : ''}
                />
                {!isValidUrl(tapLinkInput) && (
                  <p className="text-xs text-red-500">Please enter a valid URL starting with http:// or https://</p>
                )}
              </div>
              {editTarget.status !== 'active' && (
                <p className="text-xs text-muted-foreground">
                  Use <strong>Save &amp; Activate</strong> to set the TAP link and mark this campaign as active at the same time.
                </p>
              )}
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={closeEdit} disabled={saveMutation.isPending}>
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={() => editTarget && saveMutation.mutate({ id: editTarget.id, tap_affiliate_link: tapLinkInput, activate: false, old_tap_affiliate_link: editTarget.tap_affiliate_link, old_status: editTarget.status, campaign_name: campaignName(editTarget) })}
              disabled={saveMutation.isPending || !isValidUrl(tapLinkInput)}
            >
              {saveMutation.isPending ? 'Saving...' : 'Save TAP Link'}
            </Button>
            {editTarget?.status !== 'active' && (
              <Button
                className="bg-green-600 hover:bg-green-700"
                onClick={() => editTarget && saveMutation.mutate({ id: editTarget.id, tap_affiliate_link: tapLinkInput, activate: true, old_tap_affiliate_link: editTarget.tap_affiliate_link, old_status: editTarget.status, campaign_name: campaignName(editTarget) })}
                disabled={saveMutation.isPending || !tapLinkInput.trim() || !isValidUrl(tapLinkInput)}
              >
                {saveMutation.isPending ? 'Saving...' : 'Save & Activate'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Status change confirmation */}
      {statusConfirm && (() => {
        const { campaign, status } = statusConfirm;
        const labels: Record<CampaignStatus, { title: string; body: string; btnLabel: string; btnClass: string }> = {
          draft:    { title: 'Set to Draft',        body: 'will be moved back to draft. It will not be visible to creators.',                                                                              btnLabel: 'Confirm',   btnClass: '' },
          active:   { title: 'Activate Campaign',   body: 'will be marked as active. It will become visible and available to creators.',  btnLabel: 'Activate',  btnClass: 'bg-green-600 hover:bg-green-700' },
          paused:   { title: 'Pause Campaign',      body: 'will be paused. It will be temporarily hidden from creators and no new applications will be accepted until reactivated.',                      btnLabel: 'Pause',     btnClass: 'bg-orange-500 hover:bg-orange-600' },
          archived: { title: 'Archive Campaign',    body: 'will be archived. Archived campaigns are removed from all active views and cannot be applied to. This can be undone by changing the status manually.', btnLabel: 'Archive', btnClass: 'bg-destructive hover:bg-destructive/90' },
          pending:  { title: 'Set to Pending',      body: 'will be moved back to pending. It will no longer be visible to creators until activated again.',                                               btnLabel: 'Confirm',   btnClass: '' },
        };
        const meta = labels[status];
        return (
          <Dialog open onOpenChange={(open) => { if (!open && !statusMutation.isPending) setStatusConfirm(null); }}>
            <DialogContent className="sm:max-w-md overflow-hidden">
              <DialogHeader className="min-w-0">
                <DialogTitle>{meta.title}</DialogTitle>
                <DialogDescription className="pt-1 space-y-2">
                  <span className="block font-medium text-foreground break-all">
                    "{campaignName(campaign)}"
                  </span>
                  <span className="block">{meta.body}</span>
                </DialogDescription>
              </DialogHeader>
              <DialogFooter className="gap-2 pt-4">
                <Button variant="outline" onClick={() => setStatusConfirm(null)} disabled={statusMutation.isPending}>
                  Cancel
                </Button>
                <Button
                  className={meta.btnClass}
                  disabled={statusMutation.isPending}
                  onClick={() => statusMutation.mutate({ id: campaign.id, status, tap_affiliate_link: campaign.tap_affiliate_link, old_status: campaign.status, campaign_name: campaignName(campaign) })}
                >
                  {statusMutation.isPending ? 'Saving...' : meta.btnLabel}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        );
      })()}

      {/* Campaign Detail Sheet */}
      <Sheet open={!!selectedCampaign} onOpenChange={(open) => { if (!open) setSelectedCampaign(null); }}>
        <SheetContent className="overflow-y-auto w-full sm:max-w-lg">
          {selectedCampaign && (
            <>
              <SheetHeader className="pb-4">
                <SheetTitle className="text-lg">{campaignName(selectedCampaign)}</SheetTitle>
                <div className="pt-1 flex flex-wrap items-center gap-2">
                  <StatusBadge status={selectedCampaign.status} />
                  {isViralRemake(selectedCampaign) && <ViralRemakeBadge />}
                  {isMultiProduct(selectedCampaign) && <MultiProductBadge count={selectedCampaign.cr_campaign_products.length} />}
                </div>
              </SheetHeader>

              <div className="space-y-6">
                {/* Core info */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="space-y-1">
                    <p className="text-muted-foreground flex items-center gap-1"><Megaphone className="h-3.5 w-3.5" /> Brand</p>
                    <p className="font-medium">{selectedCampaign.brand_configs?.brand_name ?? '–'}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-muted-foreground flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" /> Created</p>
                    <p className="font-medium">{selectedCampaign.created_at ? format(new Date(selectedCampaign.created_at), 'MMM d, yyyy') : '–'}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-muted-foreground flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" /> Posted</p>
                    <p className="font-medium">{selectedCampaign.posted_at ? format(new Date(selectedCampaign.posted_at), 'MMM d, yyyy') : <span className="text-muted-foreground italic">Not posted</span>}</p>
                  </div>
                  {selectedCampaign.updated_at && (
                    <div className="space-y-1">
                      <p className="text-muted-foreground">Last Updated</p>
                      <p className="font-medium">{format(new Date(selectedCampaign.updated_at), 'MMM d, yyyy')}</p>
                    </div>
                  )}
                  <div className="space-y-1">
                    <p className="text-muted-foreground">Applications</p>
                    <p className="font-medium">{selectedCampaign.applications_count ?? 0}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-muted-foreground">Waitlisted</p>
                    <p className="font-medium">{selectedCampaign.waitlisted_count ?? 0}</p>
                  </div>
                </div>

                <Separator />

                {/* TAP Link */}
                <div className="space-y-2">
                  <p className="text-sm font-semibold">TAP Affiliate Link</p>
                  {selectedCampaign.tap_affiliate_link ? (
                    <a
                      href={selectedCampaign.tap_affiliate_link}
                      target="_blank"
                      rel="noreferrer"
                      className="text-blue-600 hover:underline text-sm flex items-center gap-1.5 break-all"
                    >
                      <ExternalLink className="h-3.5 w-3.5 shrink-0" />
                      {selectedCampaign.tap_affiliate_link}
                    </a>
                  ) : (
                    <p className="text-sm text-muted-foreground italic">Not set</p>
                  )}
                </div>

                {/* Products — dedicated section for a multi-product campaign, since
                    the generic "Additional Details" walker below only ever has
                    room for the campaign's OWN product_name/product_id/
                    product_image_urls/product_description columns, which are just
                    product #1 (mirrored for backward compat). A single-product
                    campaign doesn't hit this branch — it keeps rendering through
                    the generic walker exactly as before. */}
                {/* Sample bundle — the single TikTok Shop listing a creator on a
                    multi-product campaign requests their ONE sample against.
                    Its own section above Products because that is the order the
                    brand fills it in, and because support's most common
                    question on these jobs ("which ID does the creator actually
                    request?") is answered by this one row. Keyed off the
                    is_multi_product column, not the product count: the two can
                    disagree on a legacy row, and the column is what the sample
                    sync actually reads. */}
                {(selectedCampaign as any).is_multi_product && (
                  <>
                    <Separator />
                    <div className="space-y-1.5 rounded-lg border border-primary/40 bg-primary/5 p-3">
                      <div className="flex items-start justify-between gap-3">
                        <p className="text-sm font-semibold">
                          Sample bundle{(selectedCampaign as any).bundle_product_name ? ` — ${(selectedCampaign as any).bundle_product_name}` : ''}
                        </p>
                        {(selectedCampaign as any).bundle_product_id ? (
                          <span className="shrink-0 whitespace-nowrap rounded-full border border-border bg-muted px-2.5 py-0.5 font-mono text-[11px] text-muted-foreground">
                            ID {(selectedCampaign as any).bundle_product_id}
                          </span>
                        ) : (
                          <span className="shrink-0 whitespace-nowrap rounded-full border border-destructive/40 bg-destructive/10 px-2.5 py-0.5 text-[11px] font-semibold text-destructive">
                            Missing bundle ID
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground whitespace-pre-wrap break-words">
                        {(selectedCampaign as any).bundle_product_description
                          || 'Creators request this one listing and receive every product below — sample sync matches their hire through this ID.'}
                      </p>
                      {Array.isArray((selectedCampaign as any).bundle_product_image_urls)
                        && (selectedCampaign as any).bundle_product_image_urls.length > 0 && (
                        <div className="flex flex-wrap items-start gap-2 pt-1">
                          {(selectedCampaign as any).bundle_product_image_urls.map((url: string, j: number) => (
                            <div key={url + j} className="relative">
                              <img src={url} alt="Bundle" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                className="h-16 w-16 rounded-md object-cover border border-border shrink-0" />
                              {j === 0 && (
                                <span className="absolute inset-x-1 bottom-1 rounded bg-black/65 py-0.5 text-center text-[9px] font-bold uppercase tracking-wide text-white">
                                  Cover
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                )}

                {isMultiProduct(selectedCampaign) && (
                  <>
                    <Separator />
                    <div className="space-y-3">
                      <p className="text-sm font-semibold">Products ({selectedCampaign.cr_campaign_products.length})</p>
                      <div className="space-y-3">
                        {[...selectedCampaign.cr_campaign_products]
                          .sort((a: any, b: any) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
                          .map((p: any, i: number) => (
                            <div key={p.id} className="rounded-lg border border-border p-3">
                              <div className="flex items-start justify-between gap-3 mb-1.5">
                                <div className="flex items-center gap-2.5 min-w-0">
                                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-[11px] font-bold text-primary-foreground">
                                    {i + 1}
                                  </span>
                                  <p className="text-sm font-semibold truncate">{p.product_name || 'Untitled product'}</p>
                                </div>
                                {p.product_id && (
                                  <span className="shrink-0 whitespace-nowrap rounded-full border border-border bg-muted px-2.5 py-0.5 font-mono text-[11px] text-muted-foreground">
                                    ID {p.product_id}
                                  </span>
                                )}
                              </div>
                              {p.product_description && (
                                <p className="text-sm text-muted-foreground whitespace-pre-wrap break-words mb-3 pl-[34px]">{p.product_description}</p>
                              )}
                              {Array.isArray(p.product_image_urls) && p.product_image_urls.length > 0 && (
                                <div className="flex flex-wrap items-start gap-2 pl-[34px]">
                                  {p.product_image_urls.map((url: string, j: number) => (
                                    <div key={url + j} className="relative">
                                      <img src={url} alt="Product" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                        className="h-16 w-16 rounded-md object-cover border border-border shrink-0" />
                                      {j === 0 && (
                                        <span className="absolute inset-x-1 bottom-1 rounded bg-black/65 py-0.5 text-center text-[9px] font-bold uppercase tracking-wide text-white">
                                          Cover
                                        </span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                      </div>
                    </div>
                  </>
                )}

                {/* Extra fields — rendered in a clean card layout */}
                {(() => {
                  const skip = new Set([
                    'id', 'name', 'status', 'tap_affiliate_link',
                    'created_at', 'updated_at', 'posted_at',
                    'brand_id', 'brand_configs', 'tap_campaign_status',
                    // product_image_url is the legacy single-image column, kept in
                    // sync as product_image_urls[0] — shown merged into that field.
                    'product_image_url',
                    // cr_hires/cr_applications are the raw embedded join arrays the
                    // list query keeps around (see the campaigns query above) purely
                    // to derive hired_count/invited_count/applications_count — never
                    // meant for display. Without skipping them here, each row object
                    // (e.g. {status: 'pending'}) falls into the generic object/array
                    // branch below and renders as "[object Object]". applications_count
                    // and waitlisted_count are shown in the Core Info grid above;
                    // hired_count/invited_count are shown per-row in the table instead.
                    'cr_hires', 'cr_applications',
                    'hired_count', 'invited_count', 'waitlisted_count', 'applications_count',
                    // Shown as the Viral Video Remake badge in the sheet header
                    // above (and it re-labels Brief Videos below) — the generic
                    // renderer would otherwise print a bare "true"/"false" row.
                    'is_viral_remake',
                    // Raw embedded join array (see the campaigns query above) —
                    // only ever meant to drive isMultiProduct()/the count badge and
                    // the dedicated "Products" section below, never the generic
                    // walker (which would otherwise print "[object Object]").
                    'cr_campaign_products',
                    // Rendered by the dedicated "Sample bundle" section above —
                    // the generic walker would otherwise print a bare "true"
                    // and three unlabelled strings.
                    'is_multi_product', 'bundle_product_id', 'bundle_product_name', 'bundle_product_description',
                    'bundle_product_image_urls',
                    // A multi-product campaign's own product_name/product_id/
                    // product_image_urls/product_description columns are just
                    // product #1, mirrored for backward compat — showing them
                    // here too would silently hide products 2..N. The dedicated
                    // "Products" section below replaces them for that case; a
                    // single-product campaign is unaffected (isMultiProduct is
                    // false, so this skip is a no-op and they render exactly as
                    // before through the generic walker).
                    ...(isMultiProduct(selectedCampaign)
                      ? ['product_name', 'product_id', 'product_image_urls', 'product_description']
                      : []),
                  ]);

                  // Fields that need the full row width — tiktok_product_detail is a
                  // large nested object (long URLs, long names) that overflows a
                  // half-width grid cell if squeezed in next to another field.
                  const fullWidthFields = new Set(['product_image_urls', 'brief_link', 'discord_link', 'brief_video_urls', 'tiktok_product_detail']);

                  const labelMap: Record<string, string> = {
                    product_name:              'Product Name',
                    product_id:                'Product ID',
                    product_image_urls:        'Product Images',
                    budget_cap_cents:          'Budget Cap',
                    budget_spent_cents:        'Budget Spent',
                    budget_pending_cents:      'Budget Pending',
                    usage_rights_default_pct:  'Usage Rights %',
                    usage_rights_mode:         'Usage Rights Mode',
                    reblast_count:             'Re-blast Count',
                    blast_status:              'Blast Status',
                    // On a remake campaign these videos are the worklist, not
                    // style inspiration — the heading has to say so.
                    brief_video_urls:          isViralRemake(selectedCampaign) ? VIDEOS_TO_REMAKE_HEADING : 'Brief Videos',
                    brief_link:                'Brief Link',
                    discord_link:              'Discord Link',
                    targeting_filters:         'Targeting',
                    launch_email_sent_at:      'Email Sent At',
                    open_brief_allocation:     'Open Brief Allocation',
                    budget_allocation_percent: 'Budget Allocation %',
                  };

                  const formatLabel = (key: string) =>
                    labelMap[key] ?? key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

                  const formatCents = (n: number) =>
                    '$' + (n / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

                  const renderValue = (key: string, value: any): React.ReactNode => {
                    // Currency
                    if (key.endsWith('_cents') && typeof value === 'number') {
                      return <span className="font-mono">{formatCents(value)}</span>;
                    }
                    // Percentage
                    if (key.endsWith('_pct') && typeof value === 'number') {
                      return <span>{value}%</span>;
                    }
                    // Product images — thumbnail row, index 0 is the cover image
                    if (key === 'product_image_urls' && Array.isArray(value)) {
                      return (
                        <div className="flex flex-wrap items-start gap-3">
                          {value.map((url: string, i: number) => (
                            <div key={url + i} className="flex flex-col items-center gap-1">
                              <img src={url} alt="Product" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                className="h-16 w-16 rounded-md object-cover border border-border shrink-0" />
                              {i === 0 && <span className="text-[10px] text-muted-foreground">Cover</span>}
                            </div>
                          ))}
                        </div>
                      );
                    }
                    // URL arrays — numbered clickable links. brief_video_urls entries
                    // are {url, name, description?} objects (name is the brand-given
                    // label, shown above the link; description is the per-video
                    // remake instruction, only present on remake campaigns); other
                    // array fields are plain strings.
                    if (Array.isArray(value)) {
                      if (value.length === 0) return <span className="text-muted-foreground italic">None</span>;
                      return (
                        <div className="flex flex-col gap-2">
                          {value.map((item: any, i: number) => {
                            const isObject = item && typeof item === 'object';
                            const isVideoRef = isObject && typeof item.url === 'string';
                            // A malformed entry (object with no url) used to
                            // stringify to "[object Object]" — skip it instead.
                            if (isObject && !isVideoRef) return null;
                            const str = isVideoRef ? item.url : String(item);
                            const description = remakeDescription(item);
                            return str.startsWith('http') ? (
                              <div key={i} className="flex flex-col gap-0.5">
                                {isVideoRef && (
                                  <span className="text-xs font-medium">{item.name || `Example ${i + 1}`}</span>
                                )}
                                <a href={str} target="_blank" rel="noreferrer"
                                  className="flex items-start gap-1.5 text-blue-600 hover:underline text-xs">
                                  <span className="shrink-0 text-muted-foreground mt-px">{i + 1}.</span>
                                  <span className="break-all">{str.length > 65 ? str.slice(0, 62) + '…' : str}</span>
                                </a>
                                {description && (
                                  <div className="mt-1 rounded-md border border-sky-200 bg-sky-50/70 px-2 py-1.5 dark:border-sky-900/40 dark:bg-sky-950/30">
                                    <p className="flex items-center gap-1 text-[10px] font-semibold text-sky-700 dark:text-sky-400 uppercase tracking-wide mb-0.5">
                                      <PenLine className="h-2.5 w-2.5" />
                                      {WHAT_TO_REMAKE_LABEL}
                                    </p>
                                    <p className="text-xs font-normal text-foreground/90 whitespace-pre-wrap break-words">{description}</p>
                                  </div>
                                )}
                              </div>
                            ) : <span key={i} className="text-xs break-all">{str}</span>;
                          })}
                        </div>
                      );
                    }
                    // Plain URL
                    if (typeof value === 'string' && value.startsWith('http')) {
                      return (
                        <a href={value} target="_blank" rel="noreferrer"
                          className="flex items-start gap-1 text-blue-600 hover:underline text-sm break-all">
                          <ExternalLink className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                          <span className="break-all">{value}</span>
                        </a>
                      );
                    }
                    // Objects — small ones (targeting_filters: gender/age, all short
                    // values) stay as compact pill badges like before. Larger objects
                    // or ones holding long values (tiktok_product_detail: URLs, long
                    // product names) render as a stacked key/value list instead —
                    // rounded-full pills stretch long unbroken text into an oval/blob
                    // rather than wrapping it, so they don't work past a few short keys.
                    if (typeof value === 'object' && value !== null) {
                      const entries = Object.entries(value);
                      if (entries.length === 0) return <span className="text-muted-foreground italic">–</span>;

                      const hasLongValue = entries.some(([, v]) => String(v).length > 40);
                      if (hasLongValue || entries.length > 6) {
                        return (
                          <div className="flex flex-col gap-2 rounded-md border border-border bg-muted/40 p-2.5">
                            {entries.map(([k, v]) => {
                              const str = String(v);
                              const isUrl = str.startsWith('http');
                              return (
                                <div key={k} className="flex flex-col gap-0.5 min-w-0">
                                  <span className="text-[11px] text-muted-foreground">{formatLabel(k)}</span>
                                  {isUrl ? (
                                    <a href={str} target="_blank" rel="noreferrer"
                                      className="flex items-start gap-1 text-blue-600 hover:underline text-xs break-all">
                                      <ExternalLink className="h-3 w-3 shrink-0 mt-0.5" />
                                      <span className="break-all">{str}</span>
                                    </a>
                                  ) : (
                                    <span className="text-xs font-medium text-foreground break-words">{str || '–'}</span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        );
                      }

                      return (
                        <div className="flex flex-wrap gap-1.5">
                          {entries.map(([k, v]) => (
                            <span key={k} className="inline-flex max-w-full items-center gap-1 rounded-md bg-muted px-2.5 py-0.5 text-xs font-medium border border-border">
                              <span className="text-muted-foreground capitalize shrink-0">{k}:</span>
                              <span className="capitalize text-foreground break-words">{String(v)}</span>
                            </span>
                          ))}
                        </div>
                      );
                    }
                    // Timestamps
                    if (typeof value === 'string' && key.endsWith('_at') && !isNaN(Date.parse(value))) {
                      return <span className="tabular-nums">{format(new Date(value), 'MMM d, yyyy h:mm a')}</span>;
                    }
                    // Status / mode → badge
                    if (typeof value === 'string' && (key.endsWith('_status') || key.endsWith('_mode'))) {
                      return (
                        <Badge variant="secondary" className="font-normal capitalize text-xs">
                          {value.replace(/_/g, ' ')}
                        </Badge>
                      );
                    }
                    return <span className="break-words">{String(value)}</span>;
                  };

                  // Legacy campaigns only have product_image_url populated (no urls array yet) —
                  // fall back to that single value so old campaigns still show a cover image.
                  const withImageFallback = Object.entries(selectedCampaign).map(([k, v]) => {
                    if (k === 'product_image_urls' && (!Array.isArray(v) || v.length === 0) && selectedCampaign.product_image_url) {
                      return [k, [selectedCampaign.product_image_url]] as [string, any];
                    }
                    return [k, v] as [string, any];
                  });

                  const extras = withImageFallback.filter(
                    ([k, v]) => !skip.has(k) && v !== null && v !== undefined && v !== '' &&
                      !(Array.isArray(v) && v.length === 0)
                  );
                  if (extras.length === 0) return null;

                  const gridExtras  = extras.filter(([k]) => !fullWidthFields.has(k));
                  const wideExtras  = extras.filter(([k]) =>  fullWidthFields.has(k));

                  return (
                    <>
                      <Separator />
                      <div className="space-y-3">
                        <p className="text-sm font-semibold">Additional Details</p>

                        {/* 2-column grid for short fields */}
                        {gridExtras.length > 0 && (
                          <div className="rounded-lg border border-border overflow-hidden divide-y divide-border">
                            {Array.from({ length: Math.ceil(gridExtras.length / 2) }).map((_, rowIdx) => {
                              const left  = gridExtras[rowIdx * 2];
                              const right = gridExtras[rowIdx * 2 + 1];
                              return (
                                <div key={rowIdx} className="grid grid-cols-2 divide-x divide-border">
                                  <div className="px-3 py-2.5 space-y-1 min-w-0">
                                    <p className="text-xs text-muted-foreground">{formatLabel(left[0])}</p>
                                    <div className="text-sm font-medium">{renderValue(left[0], left[1])}</div>
                                  </div>
                                  {right ? (
                                    <div className="px-3 py-2.5 space-y-1 min-w-0">
                                      <p className="text-xs text-muted-foreground">{formatLabel(right[0])}</p>
                                      <div className="text-sm font-medium">{renderValue(right[0], right[1])}</div>
                                    </div>
                                  ) : <div />}
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* Full-width items (URLs, link lists, images) */}
                        {wideExtras.length > 0 && (
                          <div className="rounded-lg border border-border overflow-hidden divide-y divide-border">
                            {wideExtras.map(([key, value]) => (
                              <div key={key} className="px-3 py-2.5 space-y-2">
                                <p className="text-xs text-muted-foreground">{formatLabel(key)}</p>
                                <div className="text-sm font-medium">{renderValue(key, value)}</div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </>
                  );
                })()}

                <Separator />

                {/* Actions */}
                <div className="space-y-2">
                  <p className="text-sm font-semibold">Actions</p>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => { openEdit(selectedCampaign); setSelectedCampaign(null); }}
                    >
                      <LinkIcon className="h-3.5 w-3.5 mr-1.5" />
                      {selectedCampaign.tap_affiliate_link ? 'Edit TAP Link' : 'Add TAP Link'}
                    </Button>
                    {selectedCampaign.status !== 'active' && (
                      <Button
                        size="sm"
                        className="bg-green-600 hover:bg-green-700"
                        onClick={() => { setStatusConfirm({ campaign: selectedCampaign, status: 'active' }); setSelectedCampaign(null); }}
                      >
                        <Play className="h-3.5 w-3.5 mr-1.5" /> Activate
                      </Button>
                    )}
                    {selectedCampaign.status !== 'paused' && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-orange-600 border-orange-200 hover:bg-orange-50"
                        onClick={() => { setStatusConfirm({ campaign: selectedCampaign, status: 'paused' }); setSelectedCampaign(null); }}
                      >
                        <Pause className="h-3.5 w-3.5 mr-1.5" /> Pause
                      </Button>
                    )}
                    {selectedCampaign.status !== 'archived' && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-destructive border-destructive/30 hover:bg-destructive/10"
                        onClick={() => { setStatusConfirm({ campaign: selectedCampaign, status: 'archived' }); setSelectedCampaign(null); }}
                      >
                        <Archive className="h-3.5 w-3.5 mr-1.5" /> Archive
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
